import {
  Divider,
  GKButton,
  GKDatePicker,
  Grid2,
  NoData,
  Show,
  SimpleLoader,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { exportArrayBufferToExcel } from "utils/exportCsv";
import { handleDowloadFile } from "utils/fileHelper";

const ViewReportsTab = () => {
  const { id } = useParams();

  const { data, isFetching } = useFetchQuery({
    key: ["BASKET_REPORTS"],
    route: "/model_portfolio/basket_reports_list/",
  });

  const form = useForm({
    defaultValues: {
      report: {
        reportName: "",
        name: "",
        description: "",
      },
      startDate: "",
      endDate: "",
      exportType: "",
    },
  });

  const generateBasketReport = useMutation({
    mutationKey: ["GENERATE_REPORT"],
    mutationFn: (data: any) =>
      axiosInstance.post(
        `/model_portfolio/${id}/basket_report/`,
        { ...data, reportName: data.report.reportName },
        form.watch("exportType") === "csv" && {
          responseType: "arraybuffer",
        },
      ),
    onSuccess: async (response) => {
      if (form.watch("exportType") === "pdf") {
        handleDowloadFile({
          fileData: response.data,
          applicaionType:
            form.watch("exportType") === "csv" ? "text/csv" : "application/pdf",
          fileName: form.getValues("report.name"),
          extensionType: form.watch("exportType") === "csv" ? "csv" : "pdf",
        });
      } else {
        exportArrayBufferToExcel(response.data, form.getValues("report.name"));
      }
    },
  });

  if (isFetching) return <SimpleLoader />;

  return (
    <form
      id="basket-report-form"
      onSubmit={form.handleSubmit((values) => {
        generateBasketReport.mutate(values);
      })}
    >
      <Grid2 container size={12}>
        <Grid2 container size={4} alignContent={"flex-start"} pr={2}>
          {data?.data?.map(
            (item: {
              reportName: string;
              name: string;
              description: string;
            }) => {
              return (
                <GKButton
                  onClick={() => {
                    form.reset();
                    form.setValue("report", item);
                  }}
                  key={item?.reportName}
                  fullWidth
                  variant={"outlined"}
                  color="dark"
                  style={{
                    padding: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                    background:
                      form.getValues("report.reportName") === item?.reportName
                        ? "var(--primary-50)"
                        : "#fff",
                  }}
                >
                  <Typography
                    whiteSpace={"nowrap"}
                    fontWeight={400}
                    fontSize={14}
                    textTransform={"capitalize"}
                  >
                    {item?.name}
                  </Typography>
                </GKButton>
              );
            },
          )}
        </Grid2>

        <Grid2
          container
          size={8}
          alignContent={"flex-start"}
          borderLeft={"1px solid var(--neutral-100)"}
        >
          <Show.When isTrue={isEmpty(form.watch("report.reportName"))}>
            <Grid2 size={12} pl={2}>
              <NoData
                customImg={
                  <img loading="lazy" src="/LandingPage/noReport.svg" alt="" />
                }
                text="Report Preview"
                subText="Please select the report type for see the preview of report"
              />
            </Grid2>
          </Show.When>
          <Show.When isTrue={!isEmpty(form.watch("report.reportName"))}>
            <Grid2 container size={12} spacing={2}>
              <Grid2 size={12} px={2}>
                <Typography
                  fontWeight={600}
                  fontSize={18}
                  textTransform={"capitalize"}
                >
                  {form.watch("report.name")}
                  <Typography>
                    {form.watch("report.description") || "-"}
                  </Typography>
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Divider />
              </Grid2>
              <Show.When
                isTrue={
                  form.getValues("report.reportName") ===
                  "basket_subscribers_report"
                }
              >
                <Grid2 size={12} px={2}>
                  <Grid2 size={12} mt={1} display={"flex"} gap={2}>
                    <Controller
                      name="startDate"
                      control={form.control}
                      render={({ field }) => (
                        <GKDatePicker
                          {...field}
                          inputLabel="Start Date"
                          value={dayjs(field.value)}
                          onChange={(value: any) =>
                            field.onChange(dayjs(value).format("YYYY-MM-DD"))
                          }
                          disableFuture
                          slotProps={{
                            textField: {
                              error: false,
                              helperText: false,
                            },
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="endDate"
                      control={form.control}
                      render={({ field }) => (
                        <GKDatePicker
                          {...field}
                          inputLabel="End Date"
                          value={dayjs(field.value)}
                          minDate={
                            dayjs(field.value).isValid()
                              ? dayjs(field.value)
                              : null
                          }
                          onChange={(value: any) =>
                            field.onChange(dayjs(value).format("YYYY-MM-DD"))
                          }
                          disableFuture
                          slotProps={{
                            textField: {
                              error: false,
                              helperText: false,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
              </Show.When>

              <Grid2
                size={12}
                display={"flex"}
                alignItems={"center"}
                px={2}
                gap={1}
              >
                <GKButton
                  loading={
                    generateBasketReport.isLoading &&
                    form.getValues("exportType") === "pdf"
                  }
                  variant="contained"
                  type="submit"
                  form="basket-report-form"
                  disabled={
                    form.getValues("report.reportName") !==
                    "basket_subscribers_report"
                  }
                  onClick={() => {
                    form.setValue("exportType", "pdf");
                  }}
                >
                  Generate PDF
                </GKButton>
                <GKButton
                  loading={
                    generateBasketReport.isLoading &&
                    form.getValues("exportType") === "csv"
                  }
                  variant="contained"
                  type="submit"
                  form="basket-report-form"
                  onClick={() => form.setValue("exportType", "csv")}
                >
                  Generate EXCEL
                </GKButton>
              </Grid2>
            </Grid2>
          </Show.When>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default ViewReportsTab;
